import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea,
} from "recharts";
import Navbar from "@/components/Navbar";
import { TRENDS } from "@/constants/testIds";
import {
  TrendingUp, ArrowLeft, User, Calendar, AlertTriangle,
  CheckCircle, AlertCircle, ChevronRight, Download, Loader
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static chart configuration — defined at module level to avoid creating
// new objects on every render (performance optimisation)
const LINE_CHART_MARGIN = { top: 4, right: 8, bottom: 0, left: -10 };
const X_AXIS_TICK = { fontSize: 9, fill: "#94A3B8" };
const Y_AXIS_TICK = { fontSize: 9, fill: "#94A3B8" };
const ACTIVE_DOT  = { r: 6, fill: "#1E3A8A" };

// ── helpers ──────────────────────────────────────────────────────────────────

function parseRefRange(range = "") {
  const dash = range.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/);
  if (dash) return { low: parseFloat(dash[1]), high: parseFloat(dash[2]) };
  const gt = range.match(/^[>≥]([\d.]+)$/);
  if (gt)  return { low: parseFloat(gt[1]),  high: null };
  const lt = range.match(/^[<≤]([\d.]+)$/);
  if (lt)  return { low: null, high: parseFloat(lt[1]) };
  return { low: null, high: null };
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return iso; }
}

function buildTrends(reports) {
  const markerMap = {};
  reports.forEach(r => {
    (r.blood_markers || []).forEach(m => {
      const val = parseFloat(m.value);
      if (isNaN(val)) return;
      if (!markerMap[m.name]) markerMap[m.name] = { unit: m.unit, refRange: m.reference_range, pts: [] };
      markerMap[m.name].pts.push({ date: fmtDate(r.created_at), fullDate: r.created_at, value: val, status: m.status, reportId: r._id });
    });
  });
  return Object.entries(markerMap)
    .filter(([, t]) => t.pts.length >= 2)
    .sort((a, b) => {
      const aAbn = a[1].pts.some(p => p.status === "abnormal");
      const bAbn = b[1].pts.some(p => p.status === "abnormal");
      if (aAbn !== bAbn) return aAbn ? -1 : 1;
      const aBord = a[1].pts.some(p => p.status === "borderline");
      const bBord = b[1].pts.some(p => p.status === "borderline");
      return aBord === bBord ? 0 : aBord ? -1 : 1;
    });
}

// ── Custom chart dot ──────────────────────────────────────────────────────────

function StatusDot({ cx, cy, payload }) {
  if (!cx || !cy) return null;
  const fill = payload.status === "abnormal" ? "#EF4444" : payload.status === "borderline" ? "#F59E0B" : "#10B981";
  return <circle cx={cx} cy={cy} r={5} fill={fill} stroke="white" strokeWidth={1.5} />;
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const color = d.status === "abnormal" ? "text-red-600" : d.status === "borderline" ? "text-amber-600" : "text-emerald-600";
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1">{label}</p>
      <p className={`font-bold text-sm ${color}`}>{d.value} {unit}</p>
      <p className={`capitalize ${color}`}>{d.status}</p>
    </div>
  );
}

// ── Single marker chart ───────────────────────────────────────────────────────

function MarkerChart({ name, unit, refRange, pts }) {
  const { low, high } = parseRefRange(refRange);
  const vals = pts.map(p => p.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const pad  = (maxV - minV) * 0.25 || 0.5;
  const domLow  = Math.min(minV - pad, low  ?? minV - pad);
  const domHigh = Math.max(maxV + pad, high ?? maxV + pad);

  const hasAbnormal = pts.some(p => p.status === "abnormal");
  const hasBorderline = pts.some(p => p.status === "borderline");
  const statusBadge = hasAbnormal ? "badge-abnormal" : hasBorderline ? "badge-borderline" : "badge-normal";
  const statusLabel = hasAbnormal ? "Abnormal" : hasBorderline ? "Borderline" : "Normal";

  return (
    <div data-testid={`${TRENDS.markerChart}-${name}`}
      className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-1">
      <h3 className="text-sm font-semibold text-slate-900 truncate pr-2">
        {name.replace(/\s*\([^)]*[Ll]ipoprotein[^)]*\)/g, "").trim()}
      </h3>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${statusBadge}`}>{statusLabel}</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{unit}{refRange ? ` · ref: ${refRange}` : ""}</p>

      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={pts} margin={LINE_CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          {/* Normal range band */}
          {low != null && high != null && (
            <ReferenceArea y1={low} y2={high} fill="#ECFDF5" fillOpacity={0.5} />
          )}
          {high != null && !low && (
            <ReferenceArea y1={domLow} y2={high} fill="#ECFDF5" fillOpacity={0.5} />
          )}
          {low != null && !high && (
            <ReferenceArea y1={low} y2={domHigh} fill="#ECFDF5" fillOpacity={0.5} />
          )}
          {high != null && <ReferenceLine y={high} stroke="#FCA5A5" strokeDasharray="4 3" strokeWidth={1} />}
          {low  != null && <ReferenceLine y={low}  stroke="#FCA5A5" strokeDasharray="4 3" strokeWidth={1} />}
          <XAxis dataKey="date" tick={X_AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis domain={[domLow, domHigh]} tick={Y_AXIS_TICK} tickLine={false} axisLine={false} width={36} />
          <Tooltip content={<ChartTooltip unit={unit} />} />
          <Line
            type="monotone" dataKey="value" stroke="#1E3A8A" strokeWidth={2}
            dot={<StatusDot />} activeDot={ACTIVE_DOT}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Patient list card ─────────────────────────────────────────────────────────

function PatientCard({ name, reports, onSelect }) {
  const abnCount = reports.reduce((acc, r) =>
    acc + (r.blood_markers || []).filter(m => m.status === "abnormal").length, 0);
  const dates = reports.map(r => new Date(r.created_at)).sort((a, b) => a - b);
  const dateRange = `${fmtDate(dates[0]?.toISOString())} – ${fmtDate(dates[dates.length - 1]?.toISOString())}`;
  const lastStatus = reports[reports.length - 1]?.overall_status;
  const statusColor = lastStatus === "critical" ? "text-red-600" : lastStatus === "requires_attention" ? "text-amber-600" : "text-emerald-600";

  return (
    <button
      data-testid={`${TRENDS.patientCard}-${name}`}
      onClick={() => onSelect(name)}
      className="w-full bg-white border border-slate-200 rounded-lg p-5 text-left hover:shadow-md hover:border-blue-200 transition-all group">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <User size={18} className="text-blue-900" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
            <Calendar size={11} /> {dateRange}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xl font-bold text-slate-900">{reports.length}</p>
            <p className="text-xs text-slate-400">reports</p>
          </div>
          {abnCount > 0 && (
            <div className="text-right hidden sm:block">
              <p className={`text-xl font-bold ${statusColor}`}>{abnCount}</p>
              <p className="text-xs text-slate-400">abnormal</p>
            </div>
          )}
          <ChevronRight size={18} className="text-slate-300 group-hover:text-blue-900 transition-colors" />
        </div>
      </div>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TrendsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientParam = searchParams.get("patient") || "";

  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/trends`, { withCredentials: true })
      .then(({ data }) => setReports(data))
      .catch(() => setError("Failed to load trend data"))
      .finally(() => setLoading(false));
  }, []); // API and axios are module-level constants; state setters are stable

  // Group all reports by patient name
  const patientGroups = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      const name = r.patient_info?.name || "Unknown";
      if (!map[name]) map[name] = [];
      map[name].push(r);
    });
    return Object.entries(map)
      .filter(([, reps]) => reps.length >= 2)
      .sort((a, b) => b[1].length - a[1].length);
  }, [reports]);

  // Reports for the selected patient
  const selectedReports = useMemo(() =>
    patientParam ? reports.filter(r =>
      (r.patient_info?.name || "").toLowerCase() === patientParam.toLowerCase()
    ) : [], [reports, patientParam]);

  const trendData = useMemo(() => buildTrends(selectedReports), [selectedReports]);

  const consistentAbnormal = useMemo(() =>
    trendData.filter(([, t]) => t.pts.every(p => p.status === "abnormal")).map(([n]) => n),
    [trendData]);

  const selectPatient = (name) => setSearchParams({ patient: name });
  const clearPatient  = () => setSearchParams({});

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/trends/export`, {
        params: { patient_name: patientParam },
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = patientParam.replace(/\s+/g, "_");
      link.setAttribute("download", `trends_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Export failed silently — button will re-enable for retry
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid={TRENDS.page}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Patient list view ── */}
        {!patientParam && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Patient Trends
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Patients with 2+ reports — track marker changes over time
              </p>
            </div>

            {patientGroups.length === 0 ? (
              <div data-testid={TRENDS.emptyState} className="text-center py-20 bg-white border border-slate-200 rounded-lg">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp size={24} className="text-blue-900" />
                </div>
                <h3 className="text-base font-medium text-slate-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  No trend data yet
                </h3>
                <p className="text-slate-400 text-sm mb-5 max-w-xs mx-auto">
                  Upload at least 2 blood test reports for the same patient to see trends.
                </p>
                <button onClick={() => navigate("/upload")}
                  className="bg-blue-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors">
                  Upload a report
                </button>
              </div>
            ) : (
              <div className="space-y-3" data-testid={TRENDS.patientList}>
                {patientGroups.map(([name, reps]) => (
                  <PatientCard key={name} name={name} reports={reps} onSelect={selectPatient} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Charts view ── */}
        {patientParam && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button onClick={clearPatient}
                  className="p-2 text-slate-400 hover:text-slate-700 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all">
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {patientParam}
                  </h1>
                  <p className="text-slate-400 text-sm">
                    {selectedReports.length} reports · {fmtDate(selectedReports[0]?.created_at)} – {fmtDate(selectedReports[selectedReports.length - 1]?.created_at)}
                  </p>
                </div>
              </div>
              <button
                data-testid={TRENDS.exportButton}
                onClick={handleExportPDF}
                disabled={exporting || selectedReports.length < 2}
                className="flex items-center gap-2 bg-blue-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {exporting
                  ? <><Loader size={15} className="animate-spin" /> Exporting…</>
                  : <><Download size={15} /> Export PDF</>}
              </button>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-2xl font-bold text-slate-900">{selectedReports.length}</p>
                <p className="text-xs text-slate-400 mt-0.5">Reports analysed</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-2xl font-bold text-slate-900">{trendData.length}</p>
                <p className="text-xs text-slate-400 mt-0.5">Markers tracked</p>
              </div>
              <div className={`border rounded-lg p-4 ${
                trendData.filter(([, t]) => t.pts.some(p => p.status === "abnormal")).length > 0
                  ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
                <p className={`text-2xl font-bold ${
                  trendData.filter(([, t]) => t.pts.some(p => p.status === "abnormal")).length > 0 ? "text-red-700" : "text-slate-900"}`}>
                  {trendData.filter(([, t]) => t.pts.some(p => p.status === "abnormal")).length}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Markers ever abnormal</p>
              </div>
              <div className={`border rounded-lg p-4 ${consistentAbnormal.length > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
                <p className={`text-2xl font-bold ${consistentAbnormal.length > 0 ? "text-red-700" : "text-slate-900"}`}>
                  {consistentAbnormal.length}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Consistently abnormal</p>
              </div>
            </div>

            {/* Persistent abnormal alert */}
            {consistentAbnormal.length > 0 && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Persistently abnormal across all reports</p>
                  <p className="text-xs text-red-600 mt-0.5">{consistentAbnormal.join(", ")}</p>
                </div>
              </div>
            )}

            {/* Charts grid */}
            {trendData.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
                <p className="text-slate-400 text-sm">No shared markers found between reports.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={TRENDS.chartsGrid}>
                {trendData.map(([name, { unit, refRange, pts }]) => (
                  <MarkerChart key={name} name={name} unit={unit} refRange={refRange} pts={pts} />
                ))}
              </div>
            )}

            {/* Report timeline */}
            <div className="mt-8 bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                Report History
              </h3>
              <div className="space-y-2">
                {[...selectedReports].reverse().map((r) => {
                  const abn = (r.blood_markers || []).filter(m => m.status === "abnormal").length;
                  return (
                    <Link key={r._id} to={`/reports/${r._id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          r.overall_status === "critical" ? "bg-red-500" :
                          r.overall_status === "requires_attention" ? "bg-amber-400" : "bg-emerald-400"}`} />
                        <p className="text-sm text-slate-700">{fmtDate(r.created_at)}</p>
                        <p className="text-xs text-slate-400">{r.original_filename}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {abn > 0 && <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{abn} abnormal</span>}
                        <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-900 transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
